import React from 'react';
import classnames from 'classnames';
import styles from './dataRequestDetail.module.scss';
import { SearchEntity, ProposalEntity, RootState } from 'v2/models';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import HtmlRenderer from 'v2/common/HtmlRenderer/HtmlRenderer';
import { formatDate } from 'v2/utils/dateUtils';
import SearchProviderFilterView from '../ActiveDataRequests/ProposalRespondModal/SearchProviderFilterView';
import {
  get_data_buyer_description,
  isProposalAssignedToMe,
  isWaitingForSellerProposal,
} from 'v2/utils/proposalUtils';
import { useSelector } from 'react-redux';
import AssignToMeAlert from '../DataRequestItem/WaitingSellerActions/components/AssignToMe/AssignToMeAlert';
import DataRequestWaitingForSellerProposal from './DataRequestResponse/DataRequestWaitingForSellerProposal';
import { useProposalResponseAccepted } from 'v2/queries/useProposalResponseAccepted';
import DataProviderResponse from '../DataRequestItem/DataProviderResponse';
import { AssignedColleagueIndicator } from '../AssignedColleagueIndicator';
import { isWaitingBuyerProposal } from 'v2/utils/proposalUtils';

interface DataRequestDetailProps {
  proposal: ProposalEntity;
}

const DataRequestDetail = ({ proposal }: DataRequestDetailProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const search = proposal.search as SearchEntity;
  const isWaitingBuyer = isWaitingBuyerProposal(proposal);
  const isWaitingSeller = isWaitingForSellerProposal(proposal);

  const { data: currentProposalResponse } = useProposalResponseAccepted({
    proposalId: proposal.id,
    searchId: search.id,
  });

  const showAssignedToOther = [
    Boolean(proposal.data_provider.multiple_active_users),
    Boolean(proposal.assigned_to),
    !isProposalAssignedToMe(proposal, user),
    isWaitingSeller,
  ].every(Boolean);

  const masterOrgDescription = get_data_buyer_description(proposal, true);

  const filtersAmount = search.filters.length;

  const showButtons = [isWaitingSeller, Boolean(currentProposalResponse)].every(
    Boolean
  );

  return (
    <>
      <div
        className={classnames(
          'container-xl',
          'px-0',
          styles.dataRequestDetailsContainer,
          styles.heigh_details,
          {
            [styles.reduceHeight]: !showButtons,
          }
        )}
      >
        <div
          className={classnames(
            'd-flex fix-bg-images',
            styles.leftContent,
            styles.containerPane
          )}
        >
          {isWaitingBuyer && <AssignedColleagueIndicator proposal={proposal} />}
          {showAssignedToOther && (
            <div className="mb-5">
              <AssignToMeAlert proposal={proposal} />
            </div>
          )}
          <div className="d-flex flex-column">
            <div className={styles.section}>
              <SectionHeader
                title="Date Submitted"
                className={styles.header}
                titleClassName={styles.title}
              />
              {search.created_at && (
                <HtmlRenderer
                  source={formatDate(search.created_at, true)}
                  className={styles.info}
                />
              )}
            </div>
            <div className={styles.section}>
              <SectionHeader
                title="Description"
                className={styles.header}
                titleClassName={styles.title}
              />
              {search.description && (
                <HtmlRenderer
                  source={search.description}
                  className={classnames(
                    styles.info,
                    'markdown-preserve-spaces'
                  )}
                />
              )}
            </div>
            <div className={styles.section}>
              <SectionHeader
                title="Client Description"
                className={styles.header}
                titleClassName={styles.title}
              />
              {masterOrgDescription && (
                <HtmlRenderer
                  source={masterOrgDescription}
                  className={styles.info}
                />
              )}
            </div>
            {filtersAmount > 0 && (
              <div className={styles.section}>
                <SectionHeader
                  title={`Parameters (${filtersAmount})`}
                  className={styles.header}
                  titleClassName={styles.title}
                />
                <SearchProviderFilterView
                  filters={search.filters}
                  className={styles}
                />
              </div>
            )}
            {search.questions.length > 0 && (
              <div className={styles.section}>
                <SectionHeader
                  title="Questions"
                  className={styles.header}
                  titleClassName={styles.title}
                />
                {search.questions.map((q, i) => (
                  <span key={`question__${i}`} className={styles.questions}>
                    {i + 1}. {q.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.containerPane}>
          {isWaitingBuyer && (
            <DataProviderResponse proposal={proposal} user={user} expanded />
          )}
          {isWaitingSeller && (
            <DataRequestWaitingForSellerProposal proposal={proposal} />
          )}
        </div>
      </div>
    </>
  );
};

export default DataRequestDetail;
